// ============================================================================
// Contrats communs du noyau (lot 1 de la refonte, docs/moteur/REFONTE_MOTEUR.md §5).
//
// Une seule source : chaque contrat est un schéma zod STRICT (toute clé inconnue
// est refusée) dont le type TypeScript est dérivé. Tout ce qui sort du noyau est
// du JSON pur (aucun BigInt, aucune fonction, aucun texte français : le texte est
// produit par l'adaptateur à partir de `explanation.code`).
//
//   - sortes (types d'objets) et hypothèses / conditions (vocabulaire fermé) ;
//   - objectifs (`factor`, `solve`… + objectifs « <plugin>.<objectif> ») ;
//   - `StepCheck` : statut, relation garantie, fondement de la preuve, règle,
//     conditions, branches, trace ;
//   - budgets, état de raisonnement, enveloppe versionnée.
// ============================================================================

import { z } from 'zod';
import { RAT_TEXT_RE, cmp, ratFromText } from './exact';

/** Version du format des enveloppes et des paquets de règles. */
export const KERNEL_SCHEMA_VERSION = 1 as const;

/* ------------------------------ sortes ------------------------------ */

/**
 * Sortes (types) des objets manipulés. Fermé : un plugin ne peut ni en ajouter
 * ni en redéfinir. Une règle scalaire ne s'applique jamais à une matrice.
 */
export const SORTS = [
  'real',
  'integer',
  'natural',
  'rational',
  'complex',
  'vector',
  'matrix',
  'bool',
] as const;
export const SortSchema = z.enum(SORTS);
export type Sort = z.infer<typeof SortSchema>;

/** Sortes que l'évaluateur numérique sait échantillonner (nombres réels). */
export const SCALAR_REAL_SORTS: readonly Sort[] = ['real', 'integer', 'natural', 'rational'];

/** Écriture mathématique (lue par notre parseur), jamais du code. */
export const MathTextSchema = z.string().trim().min(1).max(400);

/* ---------------------- hypothèses et conditions ---------------------- */

export const CONDITION_KINDS = [
  'real',
  'integer',
  'natural',
  'nonzero',
  'positive',
  'negative',
  'nonnegative',
  /** nul : sert aux branches d'un cas « a > 0 / a < 0 / a = 0 » (lot 2, additif) */
  'zero',
  'distinct',
  'defined_on',
] as const;
export const ConditionKindSchema = z.enum(CONDITION_KINDS);
export type ConditionKind = z.infer<typeof ConditionKindSchema>;

/** Intervalle à bornes rationnelles exactes (« -3/2 ») ; null = infini (borne ouverte). */
export const IntervalSchema = z
  .object({
    lo: z.string().regex(RAT_TEXT_RE).nullable(),
    hi: z.string().regex(RAT_TEXT_RE).nullable(),
    loOpen: z.boolean(),
    hiOpen: z.boolean(),
  })
  .strict()
  .superRefine((iv, ctx) => {
    if (iv.lo === null && !iv.loOpen)
      ctx.addIssue({ code: 'custom', path: ['loOpen'], message: 'borne infinie fermée' });
    if (iv.hi === null && !iv.hiOpen)
      ctx.addIssue({ code: 'custom', path: ['hiOpen'], message: 'borne infinie fermée' });
    if (iv.lo !== null && iv.hi !== null) {
      const c = cmp(ratFromText(iv.lo)!, ratFromText(iv.hi)!);
      if (c > 0 || (c === 0 && (iv.loOpen || iv.hiOpen)))
        ctx.addIssue({ code: 'custom', path: ['hi'], message: 'intervalle vide' });
    }
  });
export type Interval = z.infer<typeof IntervalSchema>;

/**
 * Une condition porte sur des termes écrits en notation mathématique :
 * `{ kind: 'nonzero', of: ['x − 1'] }`, `{ kind: 'distinct', of: ['A', 'B'] }`.
 * Une hypothèse a la même forme (c'est une condition supposée vraie).
 */
export const ConditionSchema = z
  .object({
    kind: ConditionKindSchema,
    of: z.array(MathTextSchema).min(1).max(8),
    domain: IntervalSchema.optional(),
  })
  .strict()
  .superRefine((c, ctx) => {
    if (c.kind === 'distinct' ? c.of.length < 2 : c.of.length !== 1)
      ctx.addIssue({
        code: 'custom',
        path: ['of'],
        message: c.kind === 'distinct' ? 'au moins deux termes' : 'un seul terme',
      });
    if ((c.kind === 'defined_on') !== (c.domain !== undefined))
      ctx.addIssue({
        code: 'custom',
        path: ['domain'],
        message: 'domaine requis pour defined_on, interdit sinon',
      });
  });
export type Condition = z.infer<typeof ConditionSchema>;
export const HypothesisSchema = ConditionSchema;
export type Hypothesis = Condition;

/* ------------------------------ objectifs ------------------------------ */

/** Objectifs communs (spec §6.1). Un plugin ajoute les siens sous « <id>.<objectif> ». */
export const CORE_GOALS = [
  'factor',
  'expand',
  'simplify',
  'solve',
  'isolate_variable',
  'compute',
  'compare',
  'prove',
  'differentiate',
  'integrate',
  'find_antiderivative',
  'find_limit',
  'model',
  'verify_program',
] as const;
export const PLUGIN_ID_RE = /^[a-z][a-z0-9-]{1,39}$/;
export const PLUGIN_GOAL_RE = /^[a-z][a-z0-9-]{1,39}\.[a-z][a-z0-9_]{1,39}$/;
export const GoalKindSchema = z.union([z.enum(CORE_GOALS), z.string().regex(PLUGIN_GOAL_RE)]);
export type GoalKind = z.infer<typeof GoalKindSchema>;

export const GoalSchema = z
  .object({ kind: GoalKindSchema, target: MathTextSchema.optional() })
  .strict()
  .superRefine((g, ctx) => {
    if (g.kind === 'isolate_variable' && g.target === undefined)
      ctx.addIssue({ code: 'custom', path: ['target'], message: 'variable à isoler requise' });
  });
export type Goal = z.infer<typeof GoalSchema>;

/** Plugin propriétaire d'un objectif (« ineq.sign_table » → « ineq »), null pour un objectif commun. */
export function goalNamespace(goal: GoalKind): string | null {
  const i = goal.indexOf('.');
  return i < 0 ? null : goal.slice(0, i);
}

/* ---------------------------- résultat d'étape ---------------------------- */

export const STEP_STATUSES = ['valid', 'invalid', 'conditional', 'unknown', 'unsupported'] as const;
export const StepStatusSchema = z.enum(STEP_STATUSES);
export type StepStatus = z.infer<typeof StepStatusSchema>;

export const STEP_RELATIONS = [
  'identity',
  'equivalent',
  'implies',
  'deduction',
  'evaluation',
  'approximation',
  'none',
] as const;
export const StepRelationSchema = z.enum(STEP_RELATIONS);
export type StepRelation = z.infer<typeof StepRelationSchema>;

/** Ce qui fonde la conclusion. Honnêteté : 'sampled' n'est jamais 'exact'. */
export const PROOFS = ['exact', 'rule', 'sampled', 'none'] as const;
export const ProofSchema = z.enum(PROOFS);
export type Proof = z.infer<typeof ProofSchema>;

/** Identifiant de règle : « algebra.difference_of_squares » ; qualifié : « …@1 ». */
export const RULE_ID_RE = /^[a-z][a-z0-9_]{0,39}(\.[a-z][a-z0-9_]{0,39}){1,3}$/;
export const QUALIFIED_RULE_ID_RE =
  /^[a-z][a-z0-9_]{0,39}(\.[a-z][a-z0-9_]{0,39}){1,3}@[1-9]\d{0,2}$/;
/** Paquet qualifié : « algebra-core@1 ». */
export const QUALIFIED_PACK_ID_RE = /^[a-z][a-z0-9-]{1,39}@[1-9]\d{0,2}$/;

export const ExplanationSchema = z
  .object({
    code: z.string().regex(/^[a-z][a-z0-9_]{0,63}$/),
    params: z.record(z.string().regex(/^[a-z][a-zA-Z0-9_]{0,31}$/), z.string().max(400)).optional(),
  })
  .strict();
export type Explanation = z.infer<typeof ExplanationSchema>;

export const TraceStepSchema = z
  .object({
    from: MathTextSchema,
    to: MathTextSchema,
    relation: StepRelationSchema,
    ruleId: z.string().regex(QUALIFIED_RULE_ID_RE).optional(),
    explanation: ExplanationSchema.optional(),
  })
  .strict();
export type TraceStep = z.infer<typeof TraceStepSchema>;

export interface StepCheck {
  status: StepStatus;
  relation: StepRelation;
  proof: Proof;
  ruleId?: string;
  explanation?: Explanation;
  conditions?: Condition[];
  branches?: Branch[];
  trace?: TraceStep[];
}
export interface Branch {
  when: Condition[];
  check: StepCheck;
}

export const BranchSchema: z.ZodType<Branch> = z.lazy(() =>
  z.object({ when: z.array(ConditionSchema).min(1).max(8), check: StepCheckSchema }).strict(),
);
export const StepCheckSchema: z.ZodType<StepCheck> = z.lazy(() =>
  z
    .object({
      status: StepStatusSchema,
      relation: StepRelationSchema,
      proof: ProofSchema,
      ruleId: z.string().regex(QUALIFIED_RULE_ID_RE).optional(),
      explanation: ExplanationSchema.optional(),
      conditions: z.array(ConditionSchema).max(16).optional(),
      branches: z.array(BranchSchema).min(2).max(8).optional(),
      trace: z.array(TraceStepSchema).max(64).optional(),
    })
    .strict(),
);

export type StepCheckIssue =
  | 'valid_without_relation'
  | 'valid_without_proof'
  | 'invalid_with_relation'
  | 'undecided_with_proof'
  | 'conditional_without_conditions'
  | 'branches_outside_conditional'
  | 'rule_proof_without_rule';

/**
 * Cohérence d'un `StepCheck` (au-delà du schéma), récursive sur les branches.
 * Un vérificateur qui rend un résultat incohérent est traité comme défaillant.
 */
export function stepCheckIssues(c: StepCheck): StepCheckIssue[] {
  const out: StepCheckIssue[] = [];
  if (c.status === 'valid' && c.relation === 'none') out.push('valid_without_relation');
  if (c.status === 'valid' && c.proof === 'none') out.push('valid_without_proof');
  if (c.status === 'invalid' && c.relation !== 'none') out.push('invalid_with_relation');
  if (
    (c.status === 'unknown' || c.status === 'unsupported') &&
    (c.proof !== 'none' || c.relation !== 'none')
  )
    out.push('undecided_with_proof');
  if (c.status === 'conditional' && !c.conditions?.length && !c.branches?.length)
    out.push('conditional_without_conditions');
  if (c.branches?.length && c.status !== 'conditional') out.push('branches_outside_conditional');
  if (c.proof === 'rule' && c.ruleId === undefined) out.push('rule_proof_without_rule');
  c.branches?.forEach((b) =>
    stepCheckIssues(b.check).forEach((i) => out.includes(i) || out.push(i)),
  );
  return out;
}

/** Constructeurs des résultats sans conclusion (toujours cohérents). */
export function unknownCheck(code: string, params?: Record<string, string>): StepCheck {
  return {
    status: 'unknown',
    relation: 'none',
    proof: 'none',
    explanation: { code, ...(params && { params }) },
  };
}
export function unsupportedCheck(code: string, params?: Record<string, string>): StepCheck {
  return {
    status: 'unsupported',
    relation: 'none',
    proof: 'none',
    explanation: { code, ...(params && { params }) },
  };
}

/* ------------------------------ budgets ------------------------------ */

export const BudgetSchema = z
  .object({
    /** temps de calcul (ms) ; 0 = déjà épuisé */
    ms: z.number().int().min(0).max(60_000),
    /** nœuds explorés au plus */
    nodes: z.number().int().min(1).max(10_000_000),
    /** profondeur de recherche */
    depth: z.number().int().min(1).max(64),
    /** branches de cas au plus */
    branches: z.number().int().min(0).max(64),
  })
  .strict();
export type Budget = z.infer<typeof BudgetSchema>;

/** Budget d'un jugement dans le navigateur (lot 0 : p99 actuel ≈ 3,5 ms). */
export const DEFAULT_BUDGET: Budget = { ms: 50, nodes: 20_000, depth: 8, branches: 4 };

/* --------------------------- états et entrées --------------------------- */

/** Un état de raisonnement : énoncé + ce qu'on suppose + ce qui doit rester vrai + objectif. */
export const ReasoningStateSchema = z
  .object({
    statement: MathTextSchema,
    sort: SortSchema,
    hypotheses: z.array(ConditionSchema).max(32),
    conditions: z.array(ConditionSchema).max(32),
    goal: GoalSchema.optional(),
  })
  .strict();
export type ReasoningState = z.infer<typeof ReasoningStateSchema>;

/** Une étape à vérifier : de `from` à `to`, sur des objets de la sorte `sort`. */
export const StepInputSchema = z
  .object({
    sort: SortSchema,
    from: MathTextSchema,
    to: MathTextSchema,
    goal: GoalSchema.optional(),
    hypotheses: z.array(ConditionSchema).max(32).optional(),
  })
  .strict();
export type StepInput = z.infer<typeof StepInputSchema>;

/* ------------------------------ enveloppe ------------------------------ */

const EnvelopeBase = {
  v: z.literal(KERNEL_SCHEMA_VERSION),
  /** paquets de règles utilisés (« algebra-core@1 ») : rejouer avec les mêmes */
  packs: z.array(z.string().regex(QUALIFIED_PACK_ID_RE)).max(32),
};
export const EnvelopeSchema = z.discriminatedUnion('kind', [
  z.object({ ...EnvelopeBase, kind: z.literal('state'), body: ReasoningStateSchema }).strict(),
  z.object({ ...EnvelopeBase, kind: z.literal('check'), body: StepCheckSchema }).strict(),
]);
export type Envelope = z.infer<typeof EnvelopeSchema>;

/** Enveloppe → texte JSON. Lève une erreur si l'enveloppe ne respecte pas le contrat. */
export function encodeEnvelope(env: Envelope): string {
  return JSON.stringify(EnvelopeSchema.parse(env));
}

export type DecodeResult =
  | { ok: true; envelope: Envelope }
  | { ok: false; error: 'not_json' | 'unsupported_version' | 'invalid' };

/** Texte JSON → enveloppe validée. Une version future est refusée explicitement. */
export function decodeEnvelope(text: string): DecodeResult {
  let raw: unknown;
  try {
    raw = JSON.parse(text);
  } catch {
    return { ok: false, error: 'not_json' };
  }
  if (
    raw &&
    typeof raw === 'object' &&
    'v' in raw &&
    (raw as { v: unknown }).v !== KERNEL_SCHEMA_VERSION
  )
    return { ok: false, error: 'unsupported_version' };
  const r = EnvelopeSchema.safeParse(raw);
  return r.success ? { ok: true, envelope: r.data } : { ok: false, error: 'invalid' };
}
